import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';
import { hasModule } from 'helpers/profile';

import { ArrayHelpers, Loading, Modal, StringHelpers, SystemText } from 'e4shared';
import { limitOverTimeData } from '../../index';

import Chart from 'chart.js';
import { e4Format, e4Formats } from 'helpers/dates';

let metricsOverTimeChart = null;

export const CompetencyOverTime = ({ profile, manager, competency, show, close, entireDownline, modalInModal }) => {
    const user = useSelector(state => state.user.user);
    const currencySymbol = useSelector(state => state.user.user?.currencySymbol);
    const crmIntegration = useSelector(state => state.user.user?.crmIntegration);
    const allIntegrations = useSelector(state => state.user.user?.integrations);

    const ratings = user.companySettings?.display?.ratingRange || 5;
    const chartContainer = useRef(null);
    const [metricData, setMetricData] = useState([]);
    const [ratingData, setRatingData] = useState([]);
    const [job, setJob] = useState(0);
    const [competencyJob, setCompetencyJob] = useState(null);
    const [allJobs, setAllJobs] = useState(null);
    const [loading, setLoading] = useState(true);

    const people = () => {
        if (ArrayHelpers.existsAndHasLength(competency?.people)) {
            return competency.people.map(p => p.id);
        }

        return [profile.id];
    };

    const st = (path, ...rest) => SystemText(`User121/Competencies/Buttons/Overtime/Popup/${path}`, ...rest);

    const closeMe = () => {
        setMetricData([]);
        setRatingData([]);
        setCompetencyJob(null);
        setAllJobs(null);
        setJob(0);
        setLoading(true);
        close();
    };

    React.useEffect(() => {
        let all = [];
        if (competency && !allJobs) {
            if (competency.crmJobs) all = all.concat(competency.crmJobs);
            if (competency.integrationFields) {
                all = all.concat(
                    competency.integrationFields
                        .filter(f => allIntegrations.filter(i => i.data_Integration).some(i => i.ref === f.ref))
                        .map(j => ({ ...j, type: 'I', format: j.format, legend: j.name }))
                );
            }

            setAllJobs(all || []);
        }
    }, [competency, allIntegrations, allJobs]);

    React.useEffect(() => {
        allJobs && setCompetencyJob(allJobs[job]);
    }, [allJobs, job]);

    React.useEffect(() => {
        show &&
            competency &&
            apiGet(`average-over-time/rating/${competency.id}?people=${people()}`, user.guid, response => {
                setLoading(false);
                setRatingData(response.map(d => ({ ...d, date_Set: d.x, level: d.y })));
            });
    }, [show, profile, manager, competency, entireDownline, user.guid]);

    React.useEffect(() => {
        show &&
            competencyJob &&
            apiGet(
                `${competencyJob.type === 'I' ? 'average-over-time/field' : 'average-over-time/crm'}/${
                    competencyJob.id
                }?people=${people()}`,
                user.guid,
                response => {
                    setLoading(false);
                    setMetricData(
                        response.map(d => ({
                            ...d,
                            date_Set: d.x,
                            value: d.y,
                        }))
                    );
                }
            );
    }, [show, profile, manager, competencyJob, entireDownline, user.guid]);

    const datasets = [
        {
            label: st('Axis/Rated'),
            type: 'line',
            data: limitOverTimeData(ratingData, 'date_Set', 'level').map(r => ({ x: r.date_Set, y: r.level })),
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 1,
            yAxisID: 'A',
        },
    ];

    const yAxes = [
        {
            id: 'A',
            name: st('Axis/Rating'),
            position: 'left',
            scalePositionLeft: true,
            ticks: {
                precision: 0,

                suggestedMin: 0,
                suggestedMax: ratings,
                stepSize: 1,
            },
        },
    ];

    if (hasModule(user, 'integrations') && metricData && metricData.length > 0 && competencyJob) {
        datasets.push({
            label: competencyJob.legend,
            data: limitOverTimeData(metricData).map(r => ({
                x: r.date_Set,
                y: r.value,
            })),
            backgroundColor: 'rgba(99, 255, 132, 0.2)',
            borderColor: 'rgba(99, 255, 132, 1)',
            borderWidth: 1,
            yAxisID: 'B',
        });

        yAxes.push({
            id: 'B',
            name: competencyJob.name,
            position: 'right',
            ticks: {
                precision: 0,
                suggestedMin: 0,
                callback: function (label, index, labels) {
                    return competencyJob.format === 'Q'
                        ? label
                        : StringHelpers.formatMetric(competencyJob.format, label, currencySymbol);
                },
            },
            gridLines: { display: false },
        });
    }

    const toFixedDown = (number, digits) => {
        const re = new RegExp('(\\d+\\.\\d{' + digits + '})(\\d)'),
            m = number.toString().match(re);
        return m ? parseFloat(m[1]) : number.valueOf();
    };

    const chartData = {
        id: 'canvas',
        type: 'bar',
        data: {
            datasets,
        },
        options: {
            tooltips: {
                callbacks: {
                    title: function (tooltipItem, data) {
                        return e4Format(tooltipItem[0].xLabel, e4Formats.month);
                    },
                    label: function (tooltipItem, data) {
                        if (tooltipItem.datasetIndex === 0) {
                            return tooltipItem.yLabel;
                        } else {
                            return competencyJob
                                ? competencyJob.format === 'Q'
                                    ? toFixedDown(tooltipItem.yLabel, 2)
                                    : StringHelpers.formatMetric(competencyJob.format, tooltipItem.yLabel, currencySymbol)
                                : '-';
                        }
                    },
                },
            },
            showLine: true,
            legend: {
                display: true,
            },
            scales: {
                xAxes: [
                    {
                        type: 'time',
                        time: {
                            unit: 'month',
                        },
                    },
                ],
                yAxes,
            },
        },
    };

    React.useEffect(() => {
        if (chartContainer?.current) {
            metricsOverTimeChart?.destroy();
            metricsOverTimeChart = new Chart(chartContainer.current, chartData);
        }
    }, [metricData, ratingData, chartContainer, chartData]);

    const moveJob = dir => {
        let temp = job + dir;
        if (temp < 0) temp = allJobs.length - 1;
        if (temp === allJobs.length) temp = 0;
        setJob(temp);
    };

    const footer = (
        <div className="flex flex-center">
            <div className="flex-grow">
                {hasModule(user, 'integrations') && allJobs && allJobs.length > 1 && competencyJob ? (
                    <div className="pull-left flex flex-center">
                        <button className="btn btn-sm mr10" data-testid="modal-prev-button" onClick={() => moveJob(-1)}>
                            <i className="fa fa-arrow-left"></i>
                        </button>
                        {competencyJob.type === 'I' ? (
                            <img
                                className="mr10"
                                style={{ height: '25px' }}
                                src={`/dist/img/${competencyJob.ref.toLowerCase()}-icon.png`}
                                alt=""
                            />
                        ) : (
                            <img
                                className="mr10"
                                style={{ height: '25px' }}
                                src={`/dist/img/${crmIntegration.toLowerCase()}-icon.png`}
                                alt=""
                            />
                        )}
                        {competencyJob.name}
                        <button data-testid="modal-next-button" className="btn btn-sm ml10" onClick={() => moveJob(1)}>
                            <i className="fa fa-arrow-right"></i>
                        </button>
                    </div>
                ) : undefined}
            </div>
            <button className="btn" data-testid="modal-button-close" onClick={() => closeMe()}>
                {st('Close')}
            </button>
        </div>
    );

    return (
        <Modal show={show} close={closeMe} title={st('Title')} modalInModal={modalInModal} footer={footer}>
            {!loading ? (
                ratingData?.length || metricData?.length ? (
                    <canvas ref={chartContainer} width="100%" height="50%" />
                ) : (
                    <div>{st('NoData')}</div>
                )
            ) : (
                <Loading />
            )}
        </Modal>
    );
};

export default CompetencyOverTime;
